import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { TabComponent } from '../tab/tab.component';

type TabInfo = { label: string; touched: boolean };
@Component({
  selector: 'app-tabs',
  imports: [CommonModule],
  templateUrl: './tabs.component.html',
  styleUrl: './tabs.component.scss',
})
export class TabsComponent implements AfterViewInit {
  private renderer = inject(Renderer2);
  @ViewChild('tabContent') private tabContent!: ElementRef<HTMLDivElement>;
  @ContentChildren(TabComponent) tabsElements!: QueryList<TabComponent>;

  /** An array of tab ids. */
  public tabs: TabInfo[] = [];

  /** The currently active tab. */
  public activeTab: string = '';

  /** The default tab to be set. */
  @Input() public defaultTab: string = '';

  @Output('onChangeTab') public currentTab = new EventEmitter<string>();

  // Add this method to your TabsComponent
  async onTabHover(index: number): Promise<void> {
    const tabData = this.tabs[index];
    const tabElement = this.tabsElements.get(index);

    if (!tabData || tabData.touched || !tabElement) return;

    tabData.touched = true;

    try {
      tabElement.loading.set(true);

      await tabElement.main();

      if (!tabElement.initialized()) {
        tabElement.initialized.set(true);
      }
    } catch (error) {
      tabElement.error =
        error instanceof Error ? error.message : 'An error occurred';
    } finally {
      tabElement.loading.set(false);
    }
  }

  /**
   * Sets the active tab based on the provided index.
   * @param tabIndex The index of the tab to be set as active.
   * @returns void
   */
  setActiveTab(tabIndex: number): void {
    const tab = this.tabs[tabIndex];

    this.activeTab = tab.label;

    this.currentTab.emit(tab.label);
    this.toggleContentVisibility();
  }

  /**
   * Toggles the visibility of the tab content based on the active tab.
   * @returns void
   */
  toggleContentVisibility(): void {
    const tabElements = Array.from(
      this.tabContent.nativeElement.children,
    ) as HTMLElement[];
    if (tabElements) {
      tabElements.forEach(async (element, index) => {
        const label = this.tabsElements.get(index)?.name as string;

        if (label !== this.activeTab) {
          this.renderer.setStyle(element, 'display', 'none');
        } else {
          this.renderer.setStyle(element, 'display', 'block');
          if (!this.tabs[index].touched) {
            this.tabs[index].touched = true;
            if (this.tabsElements.get(index) !== undefined) {
              this.tabsElements.get(index)!.loading.set(true);
              try {
                await this.tabsElements.get(index)?.main();
              } catch (error) {
                this.tabsElements.get(index)!.error =
                  error instanceof Error ? error.message : 'An error occurred';
              }
              if (!this.tabsElements.get(index)!.initialized()) {
                this.tabsElements.get(index)!.initialized.set(true);
              }
              this.tabsElements.get(index)!.loading.set(false);
            }
          }
        }
      });
    }
  }

  /**
   * Parses the tab elements from the tab content and initializes the tabs array.
   * This method should be called after the view has been initialized to ensure
   * that the tab elements are available in the DOM.
   * @returns void
   */
  async parseTabElements() {
    if (this.tabsElements.length > 0 && !this.activeTab) {
      this.activeTab = this.defaultTab || this.tabsElements.get(0)?.name || '';
    }
    console.log({
      activeTab: this.defaultTab,
    });

    this.tabs = this.tabsElements
      .map((element) => ({
        label: element.name,
        touched: this.activeTab === element.name,
      }))
      .filter((element) => element.label !== null) as TabInfo[];

    let initialActiveTabIndex = this.tabs.findIndex(
      (tab) => tab.touched === true,
    );

    if (initialActiveTabIndex === -1) {
      console.warn(
        `Ignoring defaultTab value "${this.defaultTab}" as it does not match any tab labels. Defaulting to the first tab.`,
      );
      initialActiveTabIndex = 0;
    }

    console.log(initialActiveTabIndex);
    this.setActiveTab(initialActiveTabIndex);

    console.log({ initialActiveTabIndex });

    this.tabsElements.get(initialActiveTabIndex)!.loading.set(true);
    try {
      await this.tabsElements.get(initialActiveTabIndex)?.main();
    } catch (error) {
      this.tabsElements.get(initialActiveTabIndex)!.error =
        error instanceof Error ? error.message : 'An error occurred';
    }
    this.tabsElements.get(initialActiveTabIndex)!.loading.set(false);
    this.tabsElements.get(initialActiveTabIndex)!.initialized.set(true);
    // this.tabsElements.get(initialActiveTabIndex)?.main();

    this.toggleContentVisibility();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.parseTabElements();
    });
  }
}
